import { useState, useEffect } from 'react';
import { authenticatedFetch } from '@/api/client';
import { initialIssues } from '@/lib/dummyReportData';
import type { Issue, IssueStatus } from '@/types/reportApp';
import type { ScreenshotMetadata, ReportStatus } from '@/types/report';

interface UseReportManagementOptions {
  reportId?: string;
  screen: 'loading' | 'report';
  onToast: (message: string) => void;
}

export const useReportManagement = ({ reportId: initialReportId, screen, onToast }: UseReportManagementOptions) => {
  const [packageName, setPackageName] = useState('');
  const [issues, setIssues] = useState<Issue[]>(initialReportId ? [] : initialIssues);
  const [editingIssue, setEditingIssue] = useState<string | null>(null);
  const [reportId, setReportId] = useState('');
  const [isLoadingReport, setIsLoadingReport] = useState(!!initialReportId);
  const [accessDenied, setAccessDenied] = useState(false);
  const [packageNotFound, setPackageNotFound] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [reportStatus, setReportStatus] = useState<ReportStatus>('incoming');
  const [notes, setNotes] = useState<string | null>(null);

  // Fetch report data if reportId is provided
  useEffect(() => {
    const fetchReport = async () => {
      if (initialReportId && screen === 'report') {
        setIsLoadingReport(true);
        setAccessDenied(false);
        setPackageNotFound(false);
        setHasError(false);
        try {
          console.log('📥 Fetching report by ID:', initialReportId);
          const response = await authenticatedFetch(`/api/reports/${encodeURIComponent(initialReportId)}`);
          if (!response.ok) {
            if (response.status === 403) {
              setAccessDenied(true);
            } else if (response.status === 404) {
              setPackageNotFound(true);
            } else {
              // All other HTTP errors (5xx, other unexpected status codes, etc.)
              setHasError(true);
            }
            console.error('Failed to fetch report:', response.status);
            return;
          }
          const data = await response.json();
          console.log('📦 Report data:', data);

          // Store the report ID for updates
          if (data.id) {
            setReportId(data.id);
            console.log('📋 Report ID:', data.id);
          }

          // Backend RuleResult structure
          interface RuleResult {
            passed: boolean;
            message: string;
            details?: string;
            severity: 'red' | 'orange';
            status: 'pending' | 'accepted' | 'approved' | 'dismissed';
            relevant_files?: string[] | null;
            relevant_screenshots?: (string | ScreenshotMetadata)[] | null;
          }

          const transformedIssues: Issue[] = data.issues
            .filter((issue: RuleResult) => !issue.passed && issue.status !== 'dismissed')
            .map((issue: RuleResult, index: number) => {
              // Transform screenshots to Screenshot objects
              const screenshots = issue.relevant_screenshots?.map((screenshot, screenshotIndex) => {
                // Handle new format with metadata
                if (typeof screenshot === 'object' && 'image_data' in screenshot) {
                  return {
                    id: `screenshot-${data.id}-${index}-${screenshotIndex}`,
                    url: screenshot.image_data,
                    timestamp: screenshot.timecode,
                    timestampSeconds: screenshot.timestamp_seconds,
                    frameNumber: screenshot.frame_number,
                    videoFile: screenshot.video_file,
                  };
                }
                // Handle legacy format (plain URL string)
                return {
                  id: `screenshot-${data.id}-${index}-${screenshotIndex}`,
                  url: screenshot,
                };
              });

              return {
                id: `${data.id}-${index}`,
                fileNames: issue.relevant_files || [],
                flag: issue.severity as 'red' | 'orange',
                timestamps: [],
                description: issue.message,
                details: issue.details,
                status: (issue.status === 'accepted' ? 'approved' : issue.status) as IssueStatus,
                screenshots,
              };
            });

          console.log('✅ Transformed issues:', transformedIssues);
          console.log('📊 Issue statuses:', transformedIssues.map(i => ({ id: i.id, status: i.status })));
          setIssues(transformedIssues);

          // Set report status
          if (data.status) {
            setReportStatus(data.status);
            console.log('📊 Report status:', data.status);
          }

          if (data.campaign_package_name) {
            setPackageName(data.campaign_package_name);
          }

          // Set notes
          if (data.notes !== undefined) {
            setNotes(data.notes ?? null);
            console.log('📝 Notes:', data.notes);
          }
        } catch (error) {
          console.error('❌ Error fetching report:', error);
          setHasError(true);
        } finally {
          setIsLoadingReport(false);
        }
      }
    };

    fetchReport();
  }, [initialReportId, screen]);

  // Save report updates to backend (creates new version)
  const saveReportUpdate = async (updatedIssues: Issue[]) => {
    if (!reportId || !packageName) {
      console.log('⚠️ No report ID or package name, skipping save');
      return;
    }

    try {
      console.log('💾 Saving report update to backend...');

      // Transform issues back to backend format
      const backendIssues = updatedIssues.map((issue) => ({
        passed: false,
        message: issue.description,
        details: issue.details || '',
        severity: issue.flag,
        relevant_files: issue.fileNames.length > 0 ? issue.fileNames : null,
        relevant_screenshots: issue.screenshots && issue.screenshots.length > 0
          ? issue.screenshots.map(s => {
              // Preserve metadata if it exists
              if (s.timestampSeconds !== undefined && s.frameNumber !== undefined && s.timestamp) {
                return {
                  image_data: s.url,
                  timestamp_seconds: s.timestampSeconds,
                  frame_number: s.frameNumber,
                  timecode: s.timestamp,
                  video_file: s.videoFile,
                };
              }
              // Legacy format (plain URL)
              return s.url;
            })
          : null,
        status: issue.status,
      }));

      const response = await authenticatedFetch(`/api/reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_package_name: packageName,
          issues: backendIssues,
          updated_by: 'user',
          status: reportStatus,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save report: ${response.statusText}`);
      }

      const savedReport = await response.json();

      // Report ID remains unchanged (in-place update)
      console.log('✅ Report saved (in-place update):', savedReport.id);
    } catch (error) {
      console.error('❌ Failed to save report:', error);
      onToast('Warning: Failed to save changes to database');
    }
  };

  // Issue handlers
  const handleApprove = async (issueId: string) => {
    const updatedIssues = issues.map(issue =>
      issue.id === issueId ? { ...issue, status: 'approved' as IssueStatus } : issue
    );
    setIssues(updatedIssues);
    onToast('Issue approved');
    await saveReportUpdate(updatedIssues);
  };

  const handleDismiss = async (issueId: string) => {
    const updatedIssues = issues.map(issue =>
      issue.id === issueId ? { ...issue, status: 'dismissed' as IssueStatus } : issue
    );
    setIssues(updatedIssues);
    onToast('Issue dismissed');
    await saveReportUpdate(updatedIssues);
  };

  const handleMoveToPending = async (issueId: string) => {
    const updatedIssues = issues.map(issue =>
      issue.id === issueId ? { ...issue, status: 'pending' as IssueStatus } : issue
    );
    setIssues(updatedIssues);
    onToast('Moved to pending');
    await saveReportUpdate(updatedIssues);
  };

  const handleUpdateIssue = async (issueId: string, updates: Partial<Issue>) => {
    const updatedIssues = issues.map(issue =>
      issue.id === issueId ? { ...issue, ...updates } : issue
    );
    setIssues(updatedIssues);
    setEditingIssue(null);
    onToast('Issue updated');
    await saveReportUpdate(updatedIssues);
  };

  const handleAddIssue = async (newIssue: Omit<Issue, 'id' | 'status'>) => {
    const issue: Issue = {
      ...newIssue,
      id: Date.now().toString(),
      status: 'pending',
    };
    const updatedIssues = [...issues, issue];
    setIssues(updatedIssues);
    onToast('Issue added');
    await saveReportUpdate(updatedIssues);
  };

  const handleNotesUpdate = async (updatedNotes: string) => {
    if (!reportId) {
      console.log('⚠️ No report ID, skipping notes update');
      return;
    }

    try {
      console.log('💾 Saving notes update to backend...');

      const response = await authenticatedFetch(`/api/reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notes: updatedNotes,
          updated_by: 'user',
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save notes: ${response.statusText}`);
      }

      setNotes(updatedNotes);
      console.log('✅ Notes saved successfully');
    } catch (error) {
      console.error('❌ Failed to save notes:', error);
      onToast('Warning: Failed to save notes to database');
      throw error;
    }
  };

  // Derived state
  const activeIssues = issues.filter(i => i.status === 'pending' || i.status === 'approved');
  const pendingIssues = issues.filter(i => i.status === 'pending');
  const approvedIssues = issues.filter(i => i.status === 'approved');

  return {
    // State
    packageName,
    issues,
    editingIssue,
    setEditingIssue,
    reportId,
    isLoadingReport,
    accessDenied,
    packageNotFound,
    hasError,
    reportStatus,
    setReportStatus,
    notes,

    // Handlers
    handleApprove,
    handleDismiss,
    handleMoveToPending,
    handleUpdateIssue,
    handleAddIssue,
    handleNotesUpdate,

    // Derived state
    activeIssues,
    pendingIssues,
    approvedIssues,
  };
};
